import { useEffect, useState, useCallback } from "react";
import { Navbar } from "@/components/Navbar";
import { api, API, TOKEN_KEY, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/TranslationContext";
import { hasPermission, isSuperuser } from "@/lib/permissions";
import { ScheduledPackCard } from "@/components/ScheduledPackCard";
import { PatentFiledBadge, PATENT_APP_NO, PctDeadlineReminder } from "@/components/PatentFiledBadge";
import { CollapsibleGroup } from "@/components/CollapsibleGroup";
import { FeatureHint } from "@/components/FeatureHint";
import {
  ShieldCheck,
  FileSignature,
  Paperclip,
  Upload,
  Download,
  Trash2,
  Plus,
  Check,
  X,
  Clock,
  CircleSlash,
  Lock,
  FileText,
  Eye,
  Search,
  FileBadge,
  Scale,
  ClipboardCheck,
  Briefcase,
  BookOpen,
  Image as ImageIcon,
  Files,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

// Group confidential docs into scannable, collapsible sections (ordered).
const DOC_GROUPS = [
  { key: "as_filed", title: "Patent — as filed", icon: FileBadge, accent: true,
    match: (n) => n.startsWith("FILED_") },
  { key: "patent", title: "Patent — strategy, FTO & disclosures", icon: Scale,
    match: (n) => /FTO|Patent_|Invention_Disclosure|Plain_English/i.test(n) },
  { key: "methodology", title: "Proprietary methodology", icon: BookOpen,
    match: (n) => /Methodology/i.test(n) },
  { key: "compliance", title: "Compliance prep pack", icon: ClipboardCheck,
    match: (n) => /Compliance_|SOC2|ISO27001|Information_Security_Policy/i.test(n) },
  { key: "whitepapers", title: "White papers", icon: BookOpen,
    match: (n) => /White_Paper/i.test(n) },
  { key: "commercial", title: "Commercial collateral", icon: Briefcase,
    match: (n) => /ACV|Before_After|Leave_Behind|Pilot_SOW|Roadmap|Sales_Deck|Board_Pack|Security_Compliance|Brochure/i.test(n) },
  { key: "screenshots", title: "Product screenshots", icon: ImageIcon,
    match: (n) => /Screenshot/i.test(n) || /\.(png|svg|jpg|jpeg)$/i.test(n) },
  { key: "other", title: "Other documents", icon: Files, match: () => true },
];

function groupDocs(docs) {
  const groups = DOC_GROUPS.map((g) => ({ ...g, docs: [] }));
  [...docs].sort((a, b) => a.localeCompare(b)).forEach((name) => {
    const g = groups.find((grp) => grp.match(name));
    (g || groups[groups.length - 1]).docs.push(name);
  });
  return groups.filter((g) => g.docs.length > 0);
}

const DocRow = ({ name, isViewableDoc, viewSecureDoc, downloadSecureDoc }) => {
  const isPack = name.startsWith("FILED_");
  const isAck = name.includes("Acknowledgement");
  return (
    <div className="flex items-center justify-between gap-3 py-2.5" data-testid={`secure-doc-${name}`}>
      <div className="flex min-w-0 items-center gap-2">
        <FileText size={15} className={`shrink-0 ${isPack ? "text-emerald-600" : "text-ek-ink/65"}`} />
        <span className={`truncate text-sm ${isPack ? "font-semibold text-ek-ink" : "text-ek-ink/70"}`}>{name}</span>
        {isAck && (
          <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">Filing receipt</span>
        )}
        {isPack && !isAck && (
          <span className="shrink-0 rounded-full bg-ek-purple/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-ek-purple">As filed</span>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {isViewableDoc(name) && (
          <button onClick={() => viewSecureDoc(name)} className="ek-btn-outline flex items-center gap-1.5 !py-1.5 text-xs" data-testid={`view-secure-doc-${name}`}>
            <Eye size={13} /> View
          </button>
        )}
        <button onClick={() => downloadSecureDoc(name)} className="ek-btn-outline flex items-center gap-1.5 !py-1.5 text-xs" data-testid={`download-secure-doc-${name}`}>
          <Download size={13} /> Download
        </button>
      </div>
    </div>
  );
};

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-700",
  signed: "bg-emerald-100 text-emerald-700",
  declined: "bg-red-100 text-red-700",
};
const STATUS_ICON = { pending: Clock, signed: Check, declined: CircleSlash };

// Freedom-to-operate knock-out summary (links to the confidential annexes in secure_docs).
const FTO_ACTORS = [
  {
    key: "palantir",
    name: "Palantir (Foundry lineage)",
    note: "Closest to C1 · signed-manifest + recompute-and-assert + LM-isolation distinguishes",
    risk: "Low–Med",
    tone: "bg-amber-100 text-amber-700",
    doc: "ERIO_FTO_Annex_A_Palantir.pdf",
  },
  {
    key: "interos",
    name: "Interos (multi-tier risk)",
    note: "No granted claim reads on C2 · only a pending app US 2026/0052162 A1",
    risk: "Low",
    tone: "bg-emerald-100 text-emerald-700",
    doc: "ERIO_FTO_Annex_B.pdf",
  },
  {
    key: "alipay",
    name: "Alipay US 2022/0114499 A1",
    note: "Was mis-cited as Interos · actually a GAM ML patent · off-point",
    risk: "Negligible",
    tone: "bg-emerald-100 text-emerald-700",
    doc: "ERIO_FTO_Annex_B.pdf",
  },
  {
    key: "risklens",
    name: "RiskLens / SAFE (FAIR)",
    note: "FAIR is an open standard · low residual to C1 (RiskLens number TBC by counsel)",
    risk: "Low",
    tone: "bg-emerald-100 text-emerald-700",
    doc: "ERIO_FTO_PCT_Strategy.pdf",
  },
];

const Stat = ({ label, value, tone = "text-ek-ink" }) => (
  <div className="rounded-md border border-black/5 bg-white px-5 py-4">
    <div className={`text-3xl font-bold ${tone}`}>{value}</div>
    <div className="mt-1 text-xs uppercase tracking-wide text-ek-ink/65">{label}</div>
  </div>
);

const SignDialog = ({ attestation, onClose, onDone }) => {
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/attestations/${attestation.attestation_id}/sign`, {
        signature_name: name,
        agree,
      });
      toast.success("Attestation e-signed");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not sign");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4" data-testid="sign-dialog">
      <div className="w-full max-w-lg rounded-md border border-black/10 bg-white p-7 shadow-2xl">
        <div className="flex items-center gap-2 text-ek-purple">
          <FileSignature size={18} />
          <p className="ek-eyebrow">Electronic signature</p>
        </div>
        <h2 className="ek-heading mt-2 text-2xl">Sign attestation</h2>
        <p className="mt-3 rounded-md bg-ek-canvas px-4 py-3 text-sm text-ek-ink/75">
          {attestation.statement}
        </p>
        {attestation.required_role && (
          <p className="mt-2 text-xs text-amber-600">
            Must be signed by role: {attestation.required_role}
          </p>
        )}
        <label className="mt-4 block text-xs font-semibold text-ek-ink/60">Type your full name to sign</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="signature-name-input"
          placeholder="e.g. Carla Risk"
          className="mt-1 w-full rounded-sm border border-black/15 px-3 py-2 font-mono text-sm focus:border-ek-purple focus:outline-none"
        />
        <label className="mt-4 flex items-start gap-2 text-sm text-ek-ink/75">
          <input
            type="checkbox"
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
            data-testid="signature-agree"
            className="mt-1 accent-ek-purple"
          />
          I confirm the above statement is accurate and this constitutes my legally-binding electronic signature.
        </label>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-sm px-4 py-2 text-sm font-semibold text-ek-ink/60 hover:text-ek-ink">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || !agree || name.trim().length < 2}
            data-testid="sign-submit"
            className="ek-btn disabled:opacity-50"
          >
            {busy ? "Signing…" : "Apply signature"}
          </button>
        </div>
      </div>
    </div>
  );
};

const DeclineDialog = ({ attestation, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/attestations/${attestation.attestation_id}/decline`, { reason });
      toast.success("Attestation declined");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not decline");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4" data-testid="decline-dialog">
      <div className="w-full max-w-md rounded-md border border-black/10 bg-white p-7 shadow-2xl">
        <h2 className="ek-heading text-2xl">Decline attestation</h2>
        <p className="mt-2 text-sm text-ek-ink/65">{attestation.statement}</p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="decline-reason"
          rows={3}
          placeholder="Reason for declining…"
          className="mt-4 w-full rounded-sm border border-black/15 px-3 py-2 text-sm focus:border-ek-purple focus:outline-none"
        />
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-sm px-4 py-2 text-sm font-semibold text-ek-ink/60 hover:text-ek-ink">
            Cancel
          </button>
          <button onClick={submit} disabled={busy || reason.trim().length < 5} data-testid="decline-submit"
            className="rounded-sm bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
            {busy ? "…" : "Decline"}
          </button>
        </div>
      </div>
    </div>
  );
};

const CreateAttestationDialog = ({ controls, onClose, onDone }) => {
  const [controlId, setControlId] = useState(controls[0]?.control_id || "");
  const [statement, setStatement] = useState("");
  const [requiredRole, setRequiredRole] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/attestations", {
        entity_type: "control",
        entity_id: controlId,
        statement,
        required_role: requiredRole || null,
      });
      toast.success("Attestation requested");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4" data-testid="create-attestation-dialog">
      <div className="w-full max-w-lg rounded-md border border-black/10 bg-white p-7 shadow-2xl">
        <h2 className="ek-heading text-2xl">Request attestation</h2>
        <label className="mt-4 block text-xs font-semibold text-ek-ink/60">Control</label>
        <select value={controlId} onChange={(e) => setControlId(e.target.value)} data-testid="att-control-select"
          className="mt-1 w-full rounded-sm border border-black/15 px-3 py-2 text-sm focus:border-ek-purple focus:outline-none">
          {controls.map((c) => (
            <option key={c.control_id} value={c.control_id}>{c.control_id} — {c.title}</option>
          ))}
        </select>
        <label className="mt-4 block text-xs font-semibold text-ek-ink/60">Attestation statement</label>
        <textarea value={statement} onChange={(e) => setStatement(e.target.value)} rows={3} data-testid="att-statement"
          placeholder="I attest that…"
          className="mt-1 w-full rounded-sm border border-black/15 px-3 py-2 text-sm focus:border-ek-purple focus:outline-none" />
        <label className="mt-4 block text-xs font-semibold text-ek-ink/60">Required signer role (optional)</label>
        <input value={requiredRole} onChange={(e) => setRequiredRole(e.target.value)} data-testid="att-required-role"
          placeholder="e.g. cro"
          className="mt-1 w-full rounded-sm border border-black/15 px-3 py-2 text-sm focus:border-ek-purple focus:outline-none" />
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-sm px-4 py-2 text-sm font-semibold text-ek-ink/60 hover:text-ek-ink">Cancel</button>
          <button onClick={submit} disabled={busy || statement.trim().length < 10} data-testid="att-create-submit"
            className="ek-btn disabled:opacity-50">{busy ? "…" : "Request"}</button>
        </div>
      </div>
    </div>
  );
};

const AttestationRow = ({ a, canSign, setSigning, setDeclining, tx }) => {
  const Icon = STATUS_ICON[a.status] || Clock;
  return (
    <div data-testid={`attestation-${a.attestation_id}`}
      className="border-b border-black/5 py-4 last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2">
            <span className={`flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[a.status]}`}>
              <Icon size={12} /> {a.status}
            </span>
            <span className="text-xs font-mono text-ek-ink/65">{a.entity_id}</span>
            <span className="text-xs text-ek-ink/65">· {a.entity_label}</span>
            {a.required_role && (
              <span className="rounded-sm bg-ek-ink/5 px-1.5 py-0.5 text-[0.68rem] text-ek-ink/60">
                signer: {a.required_role}
              </span>
            )}
          </div>
          <p className="mt-2 text-sm text-ek-ink/80">{tx(a.statement)}</p>
          {a.status === "signed" && (
            <p className="mt-2 text-xs text-emerald-700" data-testid={`sig-${a.attestation_id}`}>
              ✔ E-signed by <strong>{a.signature_name}</strong> ({a.signed_by_name}) on{" "}
              {new Date(a.signed_at).toLocaleString()} · hash{" "}
              <span className="font-mono">{a.signature_hash?.slice(0, 16)}</span>
            </p>
          )}
          {a.status === "declined" && (
            <p className="mt-2 text-xs text-red-700">
              ✘ Declined by {a.signed_by_name}: {a.declined_reason}
            </p>
          )}
        </div>
        {a.status === "pending" && canSign && (
          <div className="flex items-center gap-2">
            <button onClick={() => setSigning(a)} data-testid={`sign-${a.attestation_id}`}
              className="ek-btn flex items-center gap-1.5 px-3 py-1.5 text-sm">
              <FileSignature size={14} /> Sign
            </button>
            <button onClick={() => setDeclining(a)} data-testid={`decline-${a.attestation_id}`}
              className="rounded-sm border border-red-200 px-3 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50">
              Decline
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

function VerifyPackModal({ onClose }) {
  const [fingerprint, setFingerprint] = useState("");
  const [framework, setFramework] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    // Normalise — copy-paste from a PDF sometimes drags whitespace / uppercase in.
    const fp = fingerprint.trim().toLowerCase().replace(/\s+/g, "");
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/governance/assurance-pack/verify", {
        fingerprint: fp,
        framework: framework || null,
      });
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Verification failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="verify-pack-modal">
      <div className="w-full max-w-lg rounded-md bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-ek-purple">Tamper-evident verification</p>
            <h2 className="mt-1 text-lg font-semibold text-ek-ink">Verify assurance pack integrity</h2>
            <p className="mt-1 text-sm text-ek-ink/70">
              Paste the SHA-256 <strong>source-data fingerprint</strong> printed on page 1 of any ERIO assurance pack.
              We&rsquo;ll recompute it against the current tenant state — if the state hasn&rsquo;t moved, they match byte-for-byte.
            </p>
          </div>
          <button onClick={onClose} className="ml-3 text-ek-ink/50 hover:text-ek-ink" data-testid="verify-pack-close" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {!result ? (
          <form onSubmit={submit} className="grid gap-3">
            <label className="text-xs font-semibold uppercase tracking-wide text-ek-ink/60">Fingerprint (64 hex chars)</label>
            <textarea
              required
              value={fingerprint}
              onChange={(e) => setFingerprint(e.target.value)}
              placeholder="e.g. fb187585d1671e6705e9d99a8e4f806c2bd7bd4e1cae369bd758d8010013b794"
              rows={2}
              spellCheck={false}
              className="rounded-md border border-black/10 px-3 py-2 font-mono text-xs"
              data-testid="verify-pack-fingerprint"
            />

            <label className="mt-1 text-xs font-semibold uppercase tracking-wide text-ek-ink/60">Framework scope (optional)</label>
            <select value={framework} onChange={(e) => setFramework(e.target.value)}
              className="rounded-md border border-black/10 px-3 py-2 text-sm" data-testid="verify-pack-framework">
              <option value="">Full ERIO catalogue</option>
              <option value="soc2">SOC 2</option>
              <option value="iso27001">ISO 27001</option>
              <option value="dora">DORA</option>
              <option value="nis2">NIS2</option>
            </select>

            {error && <p className="text-sm text-rose-700" data-testid="verify-pack-error">{error}</p>}

            <div className="mt-2 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm text-ek-ink/70 hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={busy}
                className="ek-btn inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold disabled:opacity-60"
                data-testid="verify-pack-submit">
                {busy ? "Verifying…" : "Verify"}
              </button>
            </div>
          </form>
        ) : (
          <div data-testid="verify-pack-result">
            {result.match ? (
              <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-4">
                <div className="flex items-center gap-2 text-emerald-900">
                  <Check size={18} className="text-emerald-700" />
                  <span className="text-sm font-semibold" data-testid="verify-pack-match">Match — assurance state unchanged</span>
                </div>
                <p className="mt-2 text-sm text-emerald-900/85">
                  The fingerprint you submitted matches the current live state for the
                  <span className="mx-1 font-semibold">{result.framework === "full" ? "Full catalogue" : result.framework.toUpperCase()}</span>
                  scope ({result.controls_in_scope} controls). No attestation, evidence, or scope change has occurred since the pack was minted.
                </p>
              </div>
            ) : (
              <div className="rounded-md border border-amber-200 bg-amber-50/60 p-4">
                <div className="flex items-center gap-2 text-amber-900">
                  <CircleSlash size={18} className="text-amber-700" />
                  <span className="text-sm font-semibold" data-testid="verify-pack-mismatch">Mismatch — state has moved</span>
                </div>
                <p className="mt-2 text-sm text-amber-900/85">{result.message}</p>
                <div className="mt-3 space-y-1.5 rounded-md bg-white/60 p-3 font-mono text-[11px] leading-relaxed">
                  <div><span className="mr-2 text-ek-ink/55">Submitted:</span>{result.fingerprint_submitted}</div>
                  <div><span className="mr-2 text-ek-ink/55">Current:</span><span data-testid="verify-pack-current-fp">{result.fingerprint_current}</span></div>
                </div>
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => { setResult(null); setFingerprint(""); }}
                className="rounded-md border border-black/10 px-4 py-2 text-sm text-ek-ink/70 hover:bg-slate-50"
                data-testid="verify-pack-again">
                Verify another
              </button>
              <button onClick={onClose} className="ek-btn px-4 py-2 text-sm font-semibold" data-testid="verify-pack-done">
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


export default function Governance() {
  const { user } = useAuth();
  const { tx } = useI18n();
  const canManage = hasPermission(user, "attestation.manage");
  const canSign = hasPermission(user, "attestation.sign");
  const canWriteEvidence = hasPermission(user, "evidence.write");
  const isAdmin = isSuperuser(user);  // patent / secure docs are Owner-only

  const [secureDocs, setSecureDocs] = useState([]);
  const [docSearch, setDocSearch] = useState("");
  const [viewer, setViewer] = useState(null);

  const [summary, setSummary] = useState(null);
  const [attestations, setAttestations] = useState([]);
  const [controls, setControls] = useState([]);
  const [evControl, setEvControl] = useState("");
  const [evidence, setEvidence] = useState([]);
  const [evExpanded, setEvExpanded] = useState(false);
  const [signing, setSigning] = useState(null);
  const [declining, setDeclining] = useState(null);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);

  const loadCore = useCallback(() => {
    Promise.all([
      api.get("/governance/summary"),
      api.get("/attestations"),
      api.get("/controls"),
    ]).then(([s, a, c]) => {
      setSummary(s.data);
      setAttestations(a.data.items);
      setControls(c.data.items);
      if (!evControl && c.data.items.length) setEvControl(c.data.items[0].control_id);
    });
  }, [evControl]);

  useEffect(() => {
    loadCore();
  }, [loadCore]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/secure-docs").then(({ data }) => setSecureDocs(data.items || [])).catch(() => {});
  }, [isAdmin]);

  const DOC_MIME = {
    pdf: "application/pdf",
    svg: "image/svg+xml",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    webp: "image/webp",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  };
  const docMime = (name) => DOC_MIME[name.split(".").pop().toLowerCase()] || "application/octet-stream";
  const isPdfDoc = (name) => name.toLowerCase().endsWith(".pdf");
  const isViewableDoc = (name) => /\.(pdf|png|svg)$/i.test(name);

  const viewSecureDoc = async (name) => {
    try {
      const res = await api.get(`/secure-docs/${name}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: docMime(name) }));
      setViewer({ url, name });
    } catch {
      toast.error("Could not open — admin session required");
    }
  };

  const closeViewer = () => {
    if (viewer?.url) setTimeout(() => URL.revokeObjectURL(viewer.url), 2000);
    setViewer(null);
  };

  const downloadSecureDoc = async (name) => {
    try {
      const res = await api.get(`/secure-docs/${name}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: docMime(name) }));
      // Office files (docx/pptx/xlsx) can't be rendered by the browser — always save them.
      const win = isPdfDoc(name) ? window.open(url, "_blank", "noopener,noreferrer") : null;
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Defer revoke so the browser can finish opening/saving the file.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(isPdfDoc(name) ? `Opened ${name}` : `Downloaded ${name}`);
    } catch {
      toast.error("Download failed — admin session required");
    }
  };

  const loadEvidence = useCallback(() => {
    if (!evControl) return;
    setEvExpanded(false);
    api
      .get(`/evidence?entity_type=control&entity_id=${evControl}`)
      .then(({ data }) => setEvidence(data.items));
  }, [evControl]);

  useEffect(() => {
    loadEvidence();
  }, [loadEvidence]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("entity_type", "control");
    fd.append("entity_id", evControl);
    fd.append("title", file.name);
    try {
      await api.post("/evidence/upload", fd);
      toast.success("Evidence uploaded");
      loadEvidence();
      loadCore();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeEvidence = async (id) => {
    try {
      await api.delete(`/evidence/${id}`);
      toast.success("Evidence removed");
      loadEvidence();
      loadCore();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const downloadUrl = (id) =>
    `${API}/evidence/${id}/download?auth=${localStorage.getItem(TOKEN_KEY)}`;

  const downloadAssurancePack = async (framework = null) => {
    try {
      const res = await api.get("/governance/assurance-pack",
        { responseType: "blob", params: framework ? { framework } : {} });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const label = framework || "Full";
      a.download = `ERIO-Assurance-Pack-${label}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(`${framework ? framework.toUpperCase() + " " : ""}Assurance Pack downloaded`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not generate pack");
    }
  };

  const onDialogDone = () => {
    setSigning(null);
    setDeclining(null);
    setCreating(false);
    loadCore();
  };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-12" data-testid="governance-page">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="ek-eyebrow">Trust &amp; Governance</p>
            <h1 className="ek-heading mt-2 text-4xl sm:text-5xl">Trust Centre</h1>
            <p className="mt-3 max-w-2xl text-base text-ek-ink/65">
              The control assurance spine — an <strong>evidence vault</strong> on every control and
              <strong> e-sign attestations</strong> with a tamper-evident audit trail.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          {canManage && (
            <button onClick={() => setCreating(true)} data-testid="request-attestation-button"
              className="ek-btn flex items-center gap-2">
              <Plus size={16} /> Request attestation
            </button>
          )}
          <button onClick={() => downloadAssurancePack()} data-testid="assurance-pack-button"
            className="flex items-center gap-2 rounded-sm border border-ek-purple px-4 py-2 text-sm font-semibold text-ek-purple hover:bg-ek-purple/5">
            <Download size={16} /> Full Assurance Pack
          </button>
          <button onClick={() => downloadAssurancePack("soc2")} data-testid="assurance-pack-soc2-button"
            className="flex items-center gap-2 rounded-sm border border-emerald-500 bg-emerald-50/40 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50">
            <Download size={16} /> SOC 2 pack
          </button>
          <button onClick={() => downloadAssurancePack("dora")} data-testid="assurance-pack-dora-button"
            className="flex items-center gap-2 rounded-sm border border-indigo-500 bg-indigo-50/40 px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50">
            <Download size={16} /> DORA pack
          </button>
          <button onClick={() => downloadAssurancePack("nis2")} data-testid="assurance-pack-nis2-button"
            className="flex items-center gap-2 rounded-sm border border-amber-500 bg-amber-50/40 px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-50">
            <Download size={16} /> NIS2 pack
          </button>
          <button onClick={() => setVerifyOpen(true)} data-testid="verify-pack-button"
            className="flex items-center gap-2 rounded-sm border border-slate-400 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            <ShieldCheck size={16} /> Verify pack
          </button>
          </div>
        </div>

        {/* Verify-pack modal — auditors paste the SHA-256 from the pack cover.
            This is the tamper-evidence proof loop: match=true means the
            assurance state hasn't moved since the pack was minted. */}
        {verifyOpen && (
          <VerifyPackModal onClose={() => setVerifyOpen(false)} />
        )}

        {summary && (
          <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="governance-summary">
            <Stat label="Pending signatures" value={summary.attestations.pending} tone="text-amber-600" />
            <Stat label="Signed" value={summary.attestations.signed} tone="text-emerald-600" />
            <Stat label="Evidence files" value={summary.evidence_count} />
            <Stat label="Control evidence coverage" value={`${summary.evidence_coverage}%`} tone="text-ek-purple" />
          </div>
        )}

        {/* Attestations board — grouped by status into collapsible sections */}
        <section className="mt-10">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-ek-ink">
            <FileSignature size={18} className="text-ek-purple" /> Attestations
          </h2>
          {attestations.length === 0 ? (
            <p className="mt-4 rounded-md border border-dashed border-black/10 py-10 text-center text-sm text-ek-ink/65">
              No attestations yet.
            </p>
          ) : (
            <div className="mt-4 space-y-3" data-testid="attestations-list">
              {[
                { key: "pending", title: "Open — awaiting signature", accent: true, defaultOpen: true },
                { key: "signed", title: "Closed — signed", accent: false, defaultOpen: false },
                { key: "declined", title: "Closed — declined", accent: false, defaultOpen: false },
              ].map((grp) => {
                const rows = attestations.filter((a) => a.status === grp.key);
                if (rows.length === 0) return null;
                return (
                  <CollapsibleGroup
                    key={grp.key}
                    title={grp.title}
                    count={rows.length}
                    accent={grp.accent}
                    defaultOpen={grp.defaultOpen}
                    icon={FileSignature}
                    testid={`attestations-group-${grp.key}`}
                  >
                    {rows.map((a) => (
                      <AttestationRow key={a.attestation_id} a={a} canSign={canSign}
                        setSigning={setSigning} setDeclining={setDeclining} tx={tx} />
                    ))}
                  </CollapsibleGroup>
                );
              })}
            </div>
          )}
        </section>

        {/* Evidence vault */}
        <section className="mt-12">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-ek-ink">
            <Paperclip size={18} className="text-ek-purple" /> Evidence vault
          </h2>
          <div className="mt-4 rounded-md border border-black/5 bg-white p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <select value={evControl} onChange={(e) => setEvControl(e.target.value)} data-testid="evidence-control-select"
                className="rounded-sm border border-black/15 px-3 py-2 text-sm focus:border-ek-purple focus:outline-none">
                {controls.map((c) => (
                  <option key={c.control_id} value={c.control_id}>{c.control_id} — {c.title}</option>
                ))}
              </select>
              {canWriteEvidence && (
                <label data-testid="evidence-upload-label"
                  className="ek-btn flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm">
                  <Upload size={14} /> {uploading ? "Uploading…" : "Upload evidence"}
                  <input type="file" className="hidden" onChange={upload} data-testid="evidence-file-input"
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.txt,.doc,.docx,.xls,.xlsx" disabled={uploading} />
                </label>
              )}
            </div>
            <div className="mt-4 divide-y divide-black/5" data-testid="evidence-list">
              {evidence.length === 0 ? (
                <p className="py-8 text-center text-sm text-ek-ink/65">No evidence attached to this control yet.</p>
              ) : (
                (evExpanded ? evidence : evidence.slice(0, 6)).map((e) => (
                  <div key={e.evidence_id} data-testid={`evidence-${e.evidence_id}`}
                    className="flex items-center justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-ek-ink">{e.title}</div>
                      <div className="text-xs text-ek-ink/65">
                        {e.original_filename} · {(e.size / 1024).toFixed(1)} KB · {e.uploaded_by_name} ·{" "}
                        <span className="font-mono">{e.sha256?.slice(0, 12)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <a href={downloadUrl(e.evidence_id)} target="_blank" rel="noreferrer"
                        data-testid={`download-${e.evidence_id}`}
                        className="flex items-center gap-1 rounded-sm border border-black/10 px-2.5 py-1 text-xs font-semibold text-ek-ink/70 hover:border-ek-purple hover:text-ek-purple">
                        <Download size={12} /> Download
                      </a>
                      {canWriteEvidence && (
                        <button onClick={() => removeEvidence(e.evidence_id)} data-testid={`remove-${e.evidence_id}`}
                          className="rounded-sm border border-red-200 px-2 py-1 text-red-600 hover:bg-red-50">
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            {evidence.length > 6 && (
              <button
                onClick={() => setEvExpanded((v) => !v)}
                data-testid="evidence-toggle"
                className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-black/5 bg-ek-canvas/60 py-2 text-xs font-semibold text-ek-ink/65 transition-colors hover:text-ek-purple"
              >
                {evExpanded ? (
                  <><ChevronRight size={14} className="-rotate-90" /> Show fewer</>
                ) : (
                  <><ChevronRight size={14} className="rotate-90" /> Show all {evidence.length} evidence files</>
                )}
              </button>
            )}
          </div>
        </section>

        {canManage && <ScheduledPackCard />}

        {isAdmin && (
          <section className="mt-8 rounded-md border border-ek-purple/15 bg-ek-purple/[0.03] p-5" data-testid="secure-docs-panel">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Lock size={15} className="text-ek-purple" />
                <h2 className="ek-eyebrow">Confidential — Patent programme & commercial documents</h2>
              </div>
              <PatentFiledBadge />
            </div>

            <div className="mt-3 flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50/70 p-3" data-testid="patent-filed-banner">
              <ShieldCheck size={18} className="mt-0.5 shrink-0 text-emerald-600" />
              <div className="text-sm text-emerald-900">
                <p className="font-semibold">UK patent application filed — {PATENT_APP_NO}</p>
                <p className="text-emerald-800/80">
                  Filed 09 Jun 2026 (UKIPO acknowledged). Priority date secured; combined PCT due within
                  12 months (~09 Jun 2027). The <b>FILED_</b> documents below are the authoritative as-filed
                  record.
                </p>
                <div className="mt-2">
                  <PctDeadlineReminder />
                </div>
              </div>
            </div>

            <div className="mt-4" data-testid="fto-knockout-card">
              <p className="ek-eyebrow text-ek-ink/60">Freedom-to-operate · knock-out summary</p>
              <div className="mt-2 divide-y divide-black/5 rounded-md border border-black/5 bg-white/60">
                {FTO_ACTORS.map((a) => (
                  <div key={a.key} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`fto-actor-${a.key}`}>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ek-ink">{a.name}</p>
                      <p className="truncate text-xs text-ek-ink/65">{a.note}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${a.tone}`}>{a.risk}</span>
                      <button
                        onClick={() => downloadSecureDoc(a.doc)}
                        className="ek-btn-outline flex items-center gap-1 !py-1 text-xs"
                        data-testid={`fto-doc-${a.key}`}
                      >
                        <Download size={12} /> Annex
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <p className="mt-4 text-sm text-ek-ink/65">
              Admin-only. These files are held outside the public web root and are not indexed. The
              <b> FILED_</b> set is the as-filed record for {PATENT_APP_NO}; remaining items are supporting
              strategy and disclosure material.
            </p>

            <FeatureHint id="trust_centre_groups_v1" title="New: documents are now grouped" className="mt-3">
              The confidential library is organised into collapsible sections (all collapsed by default).
              Expand a group to act on its documents, or use the search box to find any file instantly.
            </FeatureHint>

            <div className="mt-3 flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-2">
              <Search size={15} className="shrink-0 text-ek-ink/65" />
              <input
                type="text"
                value={docSearch}
                onChange={(e) => setDocSearch(e.target.value)}
                placeholder={`Search ${secureDocs.length} documents…`}
                data-testid="secure-docs-search"
                className="w-full bg-transparent text-sm outline-none placeholder:text-ek-ink/35"
              />
              {docSearch && (
                <button onClick={() => setDocSearch("")} className="shrink-0 text-ek-ink/65 hover:text-ek-ink" data-testid="secure-docs-search-clear">
                  <X size={14} />
                </button>
              )}
            </div>

            {(() => {
              const q = docSearch.trim().toLowerCase();
              if (q) {
                const matches = secureDocs.filter((n) => n.toLowerCase().includes(q));
                return (
                  <div className="mt-3 divide-y divide-black/5 rounded-xl border border-black/5 bg-white px-4" data-testid="secure-docs-search-results">
                    {matches.length === 0
                      ? <p className="py-3 text-sm text-ek-ink/65" data-testid="secure-docs-search-empty">No documents match “{docSearch}”.</p>
                      : matches.map((name) => <DocRow key={name} name={name} isViewableDoc={isViewableDoc} viewSecureDoc={viewSecureDoc} downloadSecureDoc={downloadSecureDoc} />)}
                  </div>
                );
              }
              const groups = groupDocs(secureDocs);
              return (
                <div className="mt-3 space-y-2" data-testid="secure-docs-list">
                  {groups.map((g) => (
                    <CollapsibleGroup
                      key={g.key}
                      title={g.title}
                      count={g.docs.length}
                      icon={g.icon}
                      accent={g.accent}
                      testid={`secure-docs-group-${g.key}`}
                    >
                      <div className="divide-y divide-black/5">
                        {g.docs.map((name) => (
                          <DocRow key={name} name={name} isViewableDoc={isViewableDoc} viewSecureDoc={viewSecureDoc} downloadSecureDoc={downloadSecureDoc} />
                        ))}
                      </div>
                    </CollapsibleGroup>
                  ))}
                  {secureDocs.length === 0 && (
                    <p className="py-2 text-sm text-ek-ink/65" data-testid="secure-docs-empty">No confidential documents available.</p>
                  )}
                </div>
              );
            })()}
          </section>
        )}

        <div className="mt-8 flex items-start gap-2 text-xs text-ek-ink/65">
          <ShieldCheck size={14} className="mt-0.5 shrink-0" />
          <p>
            Every signature and decline is written to the immutable audit log with a SHA-256 hash binding the
            signer, statement, identity and timestamp — admissible evidence of who attested to what, and when.
          </p>
        </div>
      </main>

      {signing && <SignDialog attestation={signing} onClose={() => setSigning(null)} onDone={onDialogDone} />}
      {declining && <DeclineDialog attestation={declining} onClose={() => setDeclining(null)} onDone={onDialogDone} />}
      {creating && controls.length > 0 && (
        <CreateAttestationDialog controls={controls} onClose={() => setCreating(false)} onDone={onDialogDone} />
      )}

      {viewer && (
        <div
          className="fixed inset-0 z-[100] flex flex-col bg-black/70 p-4 sm:p-8"
          data-testid="doc-viewer-modal"
          onClick={closeViewer}
        >
          <div className="mx-auto flex h-full w-full max-w-5xl flex-col overflow-hidden rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 border-b border-black/5 px-5 py-3">
              <div className="flex min-w-0 items-center gap-2">
                <FileText size={16} className="shrink-0 text-ek-purple" />
                <span className="truncate text-sm font-semibold text-ek-ink">{viewer.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadSecureDoc(viewer.name)} className="ek-btn-outline flex items-center gap-1.5 !py-1.5 text-xs" data-testid="doc-viewer-download">
                  <Download size={13} /> Download
                </button>
                <button onClick={closeViewer} className="rounded-md p-1.5 text-ek-ink/65 hover:bg-black/5" data-testid="doc-viewer-close" aria-label="Close">
                  <X size={18} />
                </button>
              </div>
            </div>
            {/\.(png|jpe?g|webp)$/i.test(viewer.name) ? (
              <div className="flex h-full w-full flex-1 items-center justify-center overflow-auto bg-ek-canvas p-4">
                <img src={viewer.url} alt={viewer.name} className="max-h-full max-w-full object-contain shadow-sm" data-testid="doc-viewer-image" />
              </div>
            ) : (
              <iframe src={viewer.url} title={viewer.name} className="h-full w-full flex-1 bg-ek-canvas" data-testid="doc-viewer-frame" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
